// @ts-nocheck

import axios from 'axios';
import {
  deleteCookieKey,
  extractQueryParams,
  generateToken,
  getCookies,
  logError,
} from '../app/helpers/commonFunctions';
import {SessionDimensions} from '../logging/dimensions/SessionDimensions';
import Cookies from 'js-cookie';
import {generateABBucket} from '../app/helpers/generateAbbucket';
import Auth from 'opensooq-sso';
import {resetSocket} from '../app/modules/socket-v5/providers/socketService';

interface IRequest {
  get: (url: string, path?: string, responseType?: string) => Promise<any>;
  delete: (url: string, path?: string) => Promise<any>;
  post: (url: string, body: any, path?: string, signal?: AbortSignal) => Promise<any>;
  put: (url: string, body: any, path?: string) => Promise<any>;
}

interface IDefaultHeaders {
  source: string;
  'accept-language': string;
  'session-id': number;
  country: string;
  'x-tracking-uuid': string;
  'Content-Type': string;
  'enforce-language': string;
}

export const logout = async () => {
  deleteCookieKey('lastContacted');
  resetSocket();

  setTimeout(async () => {
    await Auth.getInstance().destroy();

    window.location.href =
      process.env.REACT_APP_OPENSOOQ_LOGOUT_URL?.replace(
        '{country}',
        getCookies().ecountry ?? 'jo'
      ).replace('{lang}', getCookies().NEXT_LOCALE === 'ku' ? 'ar' : getCookies().NEXT_LOCALE) ??
      '';
  }, 100);
};

export default class Api {
  private static async getHeaders(defaultHeaders: IDefaultHeaders): any {
    const token = await generateToken();
    const headers = {
      ...(token
        ? {
            Authorization: 'Bearer ' + token,
          }
        : {}),
      ...defaultHeaders,
    };
    return headers;
  }

  private static urlInterceptor(url: string): string {
    const hasParams = url.indexOf('?') !== -1;
    return `${url}${hasParams ? '&' : '?'}${extractQueryParams()}`;
  }

  static getInstance(
    otherSource: boolean = false,
    isBase?: boolean = false,
    customUrl = '',
    passCountry = true,
    logging = false,
    extraHeaders?: Record<string, string>
  ): {request: IRequest} {
    let baseUrl = isBase
      ? process.env.REACT_APP_OPENSOOQ_API_URL_BASE
      : !otherSource
      ? process.env.REACT_APP_OPENSOOQ_API_URL
      : process.env.REACT_APP_OPENSOOQ_API_URL_VERTICAL;
    const userAbBucket = Cookies.get('userABBucket') || generateABBucket();

    if (customUrl != '') {
      baseUrl = customUrl;
    }

    if (logging) {
      baseUrl = `${process.env.REACT_APP_NEXT_PUBLIC_LOGGING_URL_CLIENT}`;
    }

    const originalAxios = axios.create({
      baseURL: baseUrl,
    });

    let lang = 'ar';
    if (localStorage.getItem('i18nConfig')) {
      lang = localStorage.getItem('i18nConfig').indexOf('ar') > -1 ? 'ar' : 'en';
    }

    const cookiesData = getCookies();
    const defaultHeaders = {
      source: cookiesData.source,
      'accept-language': cookiesData.NEXT_LOCALE,
      'session-id': cookiesData.userInfo.id,
      country: passCountry ? cookiesData.ecountry : '',
      'x-tracking-uuid': cookiesData.device_uuid,
      'Content-Type': 'application/json',
      'enforce-language': cookiesData.NEXT_LOCALE,
      abBucket: userAbBucket,
      ...(extraHeaders || {}),
    };

    const request: IRequest = {
      get: async function (url: string, path?: string = '', responseType?: string) {
        url = Api.urlInterceptor(url);
        // Cache-bust every GET so a poisoned/stale browser-disk or CDN cache entry
        // can never be served for an API URL. A unique param forces a real network
        // request to the API origin instead of returning a cached (possibly HTML
        // fallback) response.
        url = `${url}${url.indexOf('?') !== -1 ? '&' : '?'}_=${Date.now()}`;
        const config: any = {
          headers: await Api.getHeaders(defaultHeaders),
        };
        if (responseType) {
          config.responseType = responseType;
        }
        return originalAxios.get(url, config).catch(async (error) => {
          if (error.response && error.response.status === 401) {
            await logout();
            throw error;
          }

          logError(error, url, path);

          throw error;
        });
      },
      delete: async function (url: string, path?: string = '') {
        url = Api.urlInterceptor(url);
        return originalAxios
          .delete(url, {
            headers: await Api.getHeaders(defaultHeaders),
          })
          .catch(async (error) => {
            if (error.response && error.response.status === 401) {
              await logout();
              return;
            }

            logError(error, url, path);

            throw error;
          });
      },
      post: async function (url: string, data: any, path?: string, signal?: AbortSignal) {
        const pathParam = path || '';
        url = Api.urlInterceptor(url);

        const headers = await Api.getHeaders(defaultHeaders);

        if (data instanceof FormData) {
          delete headers['Content-Type'];
        }
        const config: any = {
          headers,
        };
        if (signal) {
          config.signal = signal;
        }
        const response = await originalAxios.post(url, data, config).catch(async (error) => {
          if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
            throw error;
          }
          if (error.response && error.response.status === 401) {
            await logout();
            return;
          }

          logError(error, url, pathParam);

          throw error;
        });

        return response;
      },
      put: async function (url: string, data: any, path: string = '') {
        url = Api.urlInterceptor(url);

        const headers = await Api.getHeaders(defaultHeaders);
        if (data instanceof FormData) {
          delete headers['Content-Type'];
        }

        const response = await originalAxios
          .put(url, data, {
            headers,
          })
          .catch(async (error) => {
            if (error.response && error.response.status === 401) {
              await logout();
              return;
            }

            logError(error, url, path);

            throw error;
          });

        return response;
      },
    };
    return {
      request,
    };
  }
}
